if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item']
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      init() {
        if (this.initialized === true) return;

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;

        // Hobby Feltro: keep only the selected variant image in the viewer.
        // Other variant images are stored off-DOM until their variant is selected.
        this.hasLinkedVariantMedia = this.dataset.hfFilterVariantMedia === 'true';
        if (this.hasLinkedVariantMedia) {
          this.initFilteredVariantMedia();
          this.bindGeneralThumbnailClicks();
        }
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        this.initialized = true;
      }

      // All slides retain their Shopify media ID. A filtered thumbnail can
      // therefore open the correct slide even after the hero image changes.
      initFilteredVariantMedia() {
        this.variantSlides = new Map();
        this.generalSlides = [];
        const slides = [...this.elements.mediaList.querySelectorAll('.product__media-item')];
        slides.forEach((slide) => {
          if (slide.dataset.variantLinked === 'true') {
            this.variantSlides.set(String(slide.dataset.mediaId), slide);
            slide.remove();
          } else {
            this.generalSlides.push(slide);
          }
        });
        this.activeVariantMediaId = null;
        const initiallySelectedId = String(this.dataset.hfSelectedVariantMediaId || '');
        this.showVariantSlide(initiallySelectedId);
        this.refreshVisibleMedia();
        this.closest('.product')?.classList.toggle(
          'hf-no-gallery-thumbnails',
          this.dataset.hfNoThumbnails === 'true'
        );
      }

      showVariantSlide(mediaId) {
        if (this.activeVariantMediaId) {
          const oldSlide = this.variantSlides.get(this.activeVariantMediaId);
          if (oldSlide) {
            oldSlide.dataset.variantDisplayed = 'false';
            oldSlide.remove();
          }
        }
        let nextSlide = this.variantSlides.get(String(mediaId || ''));
        // An image-less variant should show a general product image, not the
        // picture of the last colour selected. If there is no general image,
        // retain a fallback so the product gallery never becomes empty.
        if (!nextSlide && this.generalSlides.length === 0) {
          nextSlide = this.variantSlides.values().next().value;
        }
        if (nextSlide) {
          nextSlide.dataset.variantDisplayed = 'true';
          this.elements.mediaList.prepend(nextSlide);
          this.activeVariantMediaId = String(nextSlide.dataset.mediaId);
        } else {
          this.activeVariantMediaId = null;
        }
      }

      refreshVisibleMedia() {
        this.elements.mediaItems = [
          ...this.elements.mediaList.querySelectorAll('.product__media-item')
        ];
        const singleImage = this.elements.mediaItems.length <= 1;
        this.onlyImage = singleImage;
        this.classList.toggle('hf-single-visible-media', singleImage);
        if (this.sliderInstance?.slider) {
          const slider = this.sliderInstance.slider;
          slider.update();
          slider.slideTo(0, 0, false);
          slider.allowTouchMove = !singleImage;
        }
        if (this.lightbox) {
          this.lightbox.options.dataSource = this.getLightboxDataSource();
        }
        this.syncGeneralThumbnail();
      }

      bindGeneralThumbnailClicks() {
        if (!this.elements.thumbnails) return;
        const openThumbnail = (thumb) => {
          const slides = this.elements.mediaItems || [];
          const index = slides.findIndex((slide) => slide.dataset.mediaId === thumb.dataset.target);
          if (index < 0) return;
          if (this.sliderInstance?.slider) {
            this.sliderInstance.slider.slideTo(index);
          } else {
            slides[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
          this.syncGeneralThumbnail(thumb.dataset.target);
        };
        this.elements.thumbnails.addEventListener('click', (event) => {
          const thumb = event.target.closest('.product__thumbs-item[data-target]');
          if (thumb) openThumbnail(thumb);
        });
        this.elements.thumbnails.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          const thumb = event.target.closest('.product__thumbs-item[data-target]');
          if (!thumb) return;
          event.preventDefault();
          openThumbnail(thumb);
        });
      }

      syncGeneralThumbnail(selectedId) {
        if (!this.hasLinkedVariantMedia || !this.elements.thumbnails) return;
        if (!selectedId) {
          const slider = this.sliderInstance?.slider;
          const activeSlide = slider?.slides?.[slider.activeIndex] || this.elements.mediaItems?.[0];
          selectedId = activeSlide?.dataset.mediaId;
        }
        const thumbnails = [...this.elements.thumbnails.querySelectorAll('.product__thumbs-item')];
        thumbnails.forEach((thumb) => {
          const active = thumb.dataset.target === selectedId;
          thumb.classList.toggle('swiper-slide-thumb-active', active);
          thumb.setAttribute('aria-current', active ? 'true' : 'false');
          if (active && this.thumbsInstance?.slider) {
            const thumbIndex = thumbnails.indexOf(thumb);
            this.thumbsInstance.slider.slideTo(thumbIndex);
          }
        });
      }

      getLightboxDataSource() {
        const dataSource = [];
        const mediaElements = this.hasLinkedVariantMedia
          ? this.elements.mediaItems
          : [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        for (const media of mediaElements) {
          const { mediaType, mediaIndex, src, pswpWidth, pswpHeight } = media.dataset;
          let source = { id: mediaIndex, mediaType };
          if (['model', 'video', 'external_video'].includes(mediaType)) {
            const htmlTag = mediaType === 'model' ? 'product-model' : 'video-element';
            const element = media.querySelector(htmlTag);
            if (element) {
              source.html = `<div class="pswp__item--${mediaType}">${element.outerHTML}</div>`;
            }
          } else {
            source = { ...source, src, width: pswpWidth, height: pswpHeight };
          }
          dataSource.push(source);
        }
        return dataSource;
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap
            }
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev')
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction'
          },
          threshold: 2
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal'
            }
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap
            }
          }
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap
              }
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5
              },
              1536: {
                slidesPerView: 7
              }
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            if (!this.hasLinkedVariantMedia) {
              this.sliderOptions.thumbs = {
                swiper: this.thumbsInstance.slider,
                autoScrollOffset: 2
              };
            }
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = this.getLightboxDataSource();

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
          // Don't restore focus to the opener thumbnail on close. The opener lives
          // inside the slide it was opened from, so when the tab is re-activated the
          // browser re-focuses (and scrolls) that element into view, hijacking the
          // swiper back to the opening index. Keeping focus off it prevents the jump.
          returnFocus: false
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          return this.querySelector(`.product__media-item[data-media-index="${id}"] img`) || thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data }) => {
          if (data.src) {
            return data.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          const { currIndex } = this.lightbox?.pswp;
          const slider = this.sliderInstance?.slider;
          if (slider) {
            slider.slideTo(currIndex, 0, false);
          }
        });

        this.lightbox.on('destroy', () => {
          const { currIndex } = this.lightbox?.pswp;
          if (!this.sliderInstance && currIndex !== null) {
            const target = this.hasLinkedVariantMedia
              ? this.elements.mediaItems?.[currIndex]
              : this.elements.mediaItems?.find((item) => Number(item.dataset.mediaIndex) === currIndex);
            if (target) {
              requestAnimationFrame(() => {
                const rect = target.getBoundingClientRect();
                const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
                if (!inView) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              });
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox?.pswp?.currSlide?.data?.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          const lightboxUI = this.lightbox?.pswp?.ui;

          if (!lightboxUI) return;

          if (!this.onlyImage) {
            lightboxUI.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.next();
              }
            });
            lightboxUI.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.prev();
              }
            });
          }
          lightboxUI.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox?.pswp?.close();
            }
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset?.mediaType === 'image') {
              const index = this.hasLinkedVariantMedia
                ? this.elements.mediaItems.findIndex((slide) => slide.dataset.mediaId === media.dataset.mediaId)
                : (Number(media.dataset.mediaIndex) || 0);
              if (index >= 0) this.lightbox.loadAndOpen(index);
            }
          }
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (this.hasLinkedVariantMedia) {
            this.syncGeneralThumbnail(slides[activeIndex]?.dataset.mediaId);
          } else if (thumbs?.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.hasLinkedVariantMedia && this.elements.mediaItems.length <= 1) {
          isDraggable = false;
        }
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant) return;

        let featuredMedia = variant.featured_media;

        if (!featuredMedia && variant.featured_image) {
          featuredMedia = {
            id: variant.featured_image.id,
            position: variant.featured_image.position || 0,
            media_type: 'image'
          };
        }

        if (this.hasLinkedVariantMedia) {
          // The clicked colour decides the only variant-linked slide available.
          this.showVariantSlide(featuredMedia?.id);
          this.refreshVisibleMedia();
          if (featuredMedia && !this.variantSlides.has(String(featuredMedia.id))) {
            const index = this.elements.mediaItems.findIndex(
              (slide) => String(slide.dataset.mediaId) === String(featuredMedia.id)
            );
            if (index >= 0 && this.sliderInstance?.slider) {
              this.sliderInstance.slider.slideTo(index, 0, false);
            }
          }
          return;
        }

        if (!featuredMedia) return;

        if (this.sliderInstance.slider) {
          const slideIndex = featuredMedia.position || 0;
          this.sliderInstance.slider.slideTo(slideIndex - 1, 0, false);
        } else {
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image'
                }
              : null);
        }

        if (!featuredMedia) return;

        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == featuredMedia.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
